import UAParser from 'ua-parser-js';

export function getOS() {
  const parser = new UAParser();
  const os = parser.getOS();

  if (os.name === 'Tizen') {
    return 'tizen';
  }

  if (os.name === 'WebOS') {
    return 'webos';
  }

  if (os.name === 'Chromecast' || os.name === 'Android') {
    return 'chromecast';
  }

  return 'other';
}

export default function getBrowser(userAgent?: string) {
  const parser = new UAParser(userAgent);
  const { name: engine } = parser.getEngine();

  // Amaya, Blink, EdgeHTML, Gecko, Goanna, iCab, KHTML, Links, Lynx, NetFront, NetSurf,Presto, Tasman, Trident, w3m, WebKit
  switch (engine) {
    case 'WebKit':
      return 'safari';
    case 'Blink':
      if (parser.getBrowser().name === 'Edge') {
        return 'edge';
      }
      return 'chrome';
    case 'Gecko':
      return 'firefox';
    case 'EdgeHTML':
      return 'edge';
    case 'Trident':
      return 'internet explorer';
    default:
      return 'undefined';
  }
}

async function getSupportedDRM() {
  const testKeySystems = [
    'org.w3.clearkey',
    'com.widevine.alpha',
    'com.microsoft.playready',
    'com.microsoft.playready.recommendation',
    'com.apple.fps.3_0',
    'com.apple.fps.2_0',
    'com.apple.fps.1_0',
    'com.apple.fps',
    'com.adobe.primetime',
  ];

  const basicVideoCapabilities = [
    { contentType: 'video/mp4; codecs="avc1.42E01E"' },
    { contentType: 'video/webm; codecs="vp8"' },
  ];

  const basicConfig: MediaKeySystemConfiguration = {
    initDataTypes: ['cenc'],
    videoCapabilities: basicVideoCapabilities,
  };
  const offlineConfig: MediaKeySystemConfiguration = {
    videoCapabilities: basicVideoCapabilities,
    persistentState: 'required',
    sessionTypes: ['persistent-license'],
  };

  // Try the offline config first, then fall back to the basic config.
  const configs: MediaKeySystemConfiguration[] = [offlineConfig, basicConfig];

  /** @type {!Map.<string, ?shaka.extern.DrmSupportType>} */
  const support = new Map();

  const testSystem = async (keySystem: string) => {
    try {
      const access = await navigator.requestMediaKeySystemAccess(keySystem, configs);

      const { sessionTypes } = access.getConfiguration();
      const persistentState = sessionTypes ? sessionTypes.includes('persistent-license') : false;

      support.set(keySystem, { persistentState });
      await access.createMediaKeys();
    } catch (e) {
      // Either the request failed or createMediaKeys failed.
      // Either way, write null to the support object.
      support.set(keySystem, null);
    }
  };

  // Test each key system.
  const tests = testKeySystems.map(keySystem => testSystem(keySystem));
  await Promise.all(tests);

  return (
    // Convert object to array
    Object.entries(
      // Convert map to object
      Object.fromEntries(support)
    )
      // Remove all not supported drm techs
      .filter(x => x[1] !== null)
      // return the names of the support drm techs
      .map(x => x[0])
  );
}
export async function getDRMDetection() {
  const parser = new UAParser();
  const { name: OSName, version: OSVersion } = parser.getOS();
  const { name: browserName, version: browserVersion } = parser.getBrowser();

  const DRMSupport =
    !!window.MediaKeys &&
    !!window.navigator &&
    !!window.navigator.requestMediaKeySystemAccess &&
    !!window.MediaKeySystemAccess &&
    !!window.MediaKeySystemAccess.prototype.getConfiguration;

  return {
    OSName,
    OSVersion,
    browserName,
    browserVersion,
    DRMSupport,
    supportedDRM: await getSupportedDRM(),
  };
}

export function getWebpSupport() {
  if (typeof navigator === 'undefined') return true;

  const parser = new UAParser();
  const { name, version } = parser.getBrowser();
  if (name === 'Safari' && version && parseInt(version, 10) < 14) return false;
  return true;
}

export function isMobile() {
  if (typeof navigator === 'undefined') return false;
  const agent = navigator.userAgent.toLowerCase();
  const parser = new UAParser();
  const { name, version } = parser.getOS();
  const { model } = parser.getDevice();

  if (model === 'iPad' && name === 'iOS' && version && parseInt(version, 2) >= 13) return false;

  return Boolean(/(iphone|ipad|ipod|android|windows phone)/.test(agent));
}

/**
 * This returns 'android', 'ios' or false
 */
export function mobileDevice() {
  if (!isMobile()) return false;

  const parser = new UAParser();
  const { name } = parser.getOS();
  switch (name) {
    case 'iOS':
      return 'ios';
    case 'Android':
      return 'android';
    default:
  }

  return false;
}
